import { FilterFnOption, ColumnDef } from '@tanstack/react-table'
import FbButton from 'components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { cn } from 'components/UI/cn'
import { Switch } from 'components/UI/Switch/Switch'
import { ChainSearchResult, DoorSearchResult } from 'features/elastic/searchers'
import {
  X,
  CheckCircle,
  Save,
  Download,
  Users2,
  Upload,
  Tag,
  Briefcase,
  User,
  StickyNote,
  Link,
  ArrowLeft,
} from 'lucide-react'
import { useMemo } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { oppTypesMap } from 'utils/oppTypesMap'
import { ImporterDataTable } from './importer-datatable'
import {
  CSVRow,
  ColumnType,
  CSVRowState,
  InternalColumns,
} from './importer-types'
import {
  useImporterViewModel,
  columnOptions,
  columnTypeColors,
} from './ImporterViewModel'
import { Select } from 'components/UI/Select/Select'
import { Tabs } from 'components/FbUI/Tabs'

export function ImporterTableLegacy({
  viewModel,
  importNumber,
}: {
  viewModel: ReturnType<typeof useImporterViewModel>
  importNumber: number
}) {
  const columns = useMemo(() => {
    if (!viewModel.importData) return []

    const headers = viewModel.importData.headers

    const filterFnMap: Record<string, FilterFnOption<CSVRow>> = {
      [InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID]: 'equals',
    }

    const crmDataIndicatorColumnKeys: (keyof CSVRow)[] = [
      InternalColumns.TAG_SET,
      InternalColumns.DEAL_SET,
      InternalColumns.CONTACT_SET,
      InternalColumns.COMPANY_NOTES_SET,
    ]

    const columnDefs: ColumnDef<CSVRow>[] = headers
      .filter((h) => !crmDataIndicatorColumnKeys.includes(h.original as any))
      .toSorted((a, b) => {
        if (a.original.startsWith('__internal__')) {
          return -1
        }
        if (b.original.startsWith('__internal__')) {
          return 1
        }
        return 0
      })
      .map((header) => ({
        id: header.original,
        accessorKey: header.original,
        size: 300,
        filterFn: filterFnMap[header.original],
        header: () => {
          if (header.original.startsWith('__internal__')) {
            return (
              <div className="text-xs text-gray-500">
                {header.label ?? header.original}
              </div>
            )
          }

          return (
            <div>
              <div className="flex flex-row items-center gap-2">
                <Select
                  buttonSize="xs"
                  onSelect={(value) =>
                    viewModel.updateMap(header.original, value)
                  }
                  selected={header.mapped}
                  disabled={viewModel.importPreview?.from_unmatched_chains}
                  nullSelectionLabel="Select a Column"
                  items={Object.entries(columnOptions).map(([k, v]) => ({
                    label: v.name,
                    value: k,
                    hidden:
                      viewModel.mappedColumns?.includes(k) &&
                      k !== 'company_notes',
                  }))}
                />
                {!viewModel.importPreview?.from_unmatched_chains && (
                  <FbButton
                    variant="outline"
                    size="xs"
                    onClick={() =>
                      viewModel.updateMap(header.original, 'ignore')
                    }
                    disabled={viewModel.importPreview?.from_unmatched_chains}
                  >
                    <X size={16} />
                  </FbButton>
                )}
              </div>
              <pre className="text-xs truncate max-w-[200px] pt-2">
                {header.label ?? header.original}
              </pre>
            </div>
          )
        },
        cell: (info) => {
          const row = info.row.original

          if (header.original === InternalColumns.SET_CHAIN_PROXY_ID) {
            let firstbiteId =
              (row[InternalColumns.MATCHED_CHAIN]?.type ?? '') +
              (row[InternalColumns.MATCHED_CHAIN]?.item_id ?? '')
            if (row[InternalColumns.IS_CUSTOM_COMPANY]) {
              firstbiteId = 'UG' + row[InternalColumns.SET_CHAIN_PROXY_ID]
            }
            return (
              <div
                key={info.cell.id}
                className="h-full w-full p-2 leading-none flex items-center justify-start"
              >
                <FbLink
                  to={`${oppTypesMap[(firstbiteId.substring(0, 2) as keyof typeof oppTypesMap) || 'UG']?.chainPageUrl ?? '/company/'}${firstbiteId}`}
                >
                  {row[InternalColumns.MATCHED_CHAIN]?.name ?? firstbiteId}
                </FbLink>
              </div>
            )
          }

          if (
            header.original ===
            InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID
          ) {
            const subCategory =
              viewModel.chainMergerAvailableSubcategories[
                info.row.original[
                  InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID
                ]
              ] ?? ''
            return (
              <div
                key={info.cell.id}
                className="h-full w-full p-2 leading-none flex items-center justify-start"
              >
                <div className="text-sm truncate">{subCategory}</div>
              </div>
            )
          }

          const mappedColumn = header.mapped
          const columnType = (columnOptions[mappedColumn]?.type ??
            'ignore') as ColumnType
          const color = columnTypeColors[columnType]
          return (
            <div
              key={info.cell.id}
              style={{
                backgroundColor: color.background,
                color: color.text,
              }}
              className="h-full w-full p-2 leading-none flex items-center justify-start"
            >
              {info.getValue() as React.ReactNode}
            </div>
          )
        },
      }))

    // MATCHES SELECTOR
    if (viewModel.selectedTab !== 'reviewed') {
      if (!viewModel.importPreview?.from_unmatched_chains) {
        columnDefs.unshift({
          id: 'is_custom_company',
          header: 'Custom',
          size: 270,
          cell: (info) => {
            const row = info.row.original
            const disabled = row[InternalColumns.SET_CHAIN_PROXY_ID] !== -1
            return (
              <div
                key={info.cell.id}
                className={cn(
                  'flex-1 flex gap-2 items-center justify-start h-full w-full p-2',
                  { 'opacity-20': disabled }
                )}
              >
                <Switch
                  checked={row[InternalColumns.IS_CUSTOM_COMPANY]}
                  disabled={disabled}
                  onChange={(e) => {
                    viewModel.updateIsCustomCompany(
                      [row[InternalColumns.ROW_ID]],
                      e.target.checked
                    )
                  }}
                ></Switch>
                <Select
                  buttonSize="xs"
                  className="w-[400px] max-h-[500px]"
                  disabled={!row[InternalColumns.IS_CUSTOM_COMPANY] || disabled}
                  items={viewModel.companySubcategories}
                  selected={row[InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]}
                  nullSelectionLabel="Select a Category"
                  onSelect={(value, originalItem) =>
                    viewModel.updateCustomCompanyCategory(
                      [row[InternalColumns.ROW_ID]],
                      originalItem?.category_id,
                      value
                    )
                  }
                />
              </div>
            )
          },
        })
      }

      columnDefs.unshift(
        {
          id: 'matched_chain',
          accessorKey: 'matched_chain',
          header: 'Chain Match',
          size: 250,
          cell: (info) => {
            const row = info.row.original
            const isImported = row[InternalColumns.SET_CHAIN_PROXY_ID] !== -1
            const disabled =
              isImported || row[InternalColumns.IS_CUSTOM_COMPANY]

            return (
              <div
                key={info.cell.id}
                className={cn(
                  'flex-1 flex gap-2 items-center justify-start h-full w-full p-2',
                  { 'opacity-20': disabled }
                )}
              >
                {row[InternalColumns.MATCHED_CHAIN] && (
                  <HelpTooltip
                    content={
                      <ChainInfo chain={row[InternalColumns.MATCHED_CHAIN]} />
                    }
                    delay={[0, 100]}
                  />
                )}
                {row[InternalColumns.IS_CHAIN_MANUAL_MATCH] ? (
                  <div className="text-sm truncate">
                    {row[InternalColumns.MATCHED_CHAIN]?.name}
                  </div>
                ) : (
                  <Select
                    buttonSize="xs"
                    className="w-[400px] max-h-[500px] flex-1"
                    buttonClassName="max-w-[300px] flex-1"
                    buttonVariant={
                      row[InternalColumns.MATCHED_CHAIN]?.item_id
                        ? 'primary'
                        : 'outline'
                    }
                    disabled={
                      !row[InternalColumns.SUGGESTED_CHAINS]?.length || disabled
                    }
                    nullSelectionLabel={
                      row[InternalColumns.SUGGESTED_CHAINS]?.length
                        ? `Found ${row[InternalColumns.SUGGESTED_CHAINS]?.length} chain suggestions`
                        : 'No Suggestions'
                    }
                    items={row[InternalColumns.SUGGESTED_CHAINS]?.map(
                      (chain) => ({
                        label: <ChainInfo chain={chain} />,
                        value: chain.item_id,
                        labelWhenSelected: (
                          <div className="flex flex-row gap-2">
                            <div className="text-sm truncate">{chain.name}</div>
                          </div>
                        ),
                      })
                    )}
                    onSelect={(value) => {
                      const chain = row[InternalColumns.SUGGESTED_CHAINS]?.find(
                        (c) => c.item_id === value
                      )
                      if (chain) {
                        viewModel.updateMatchedChain(
                          row[InternalColumns.ROW_ID],
                          chain,
                          false
                        )
                      }
                    }}
                    selected={row[InternalColumns.MATCHED_CHAIN]?.item_id}
                  />
                )}
                <FbButton
                  variant="outline"
                  disabled={disabled}
                  size="xs"
                  onClick={() => {
                    viewModel.updateMatchedChain(
                      row[InternalColumns.ROW_ID],
                      null,
                      false
                    )
                    viewModel.updateMatchedDoor(
                      row[InternalColumns.ROW_ID],
                      null,
                      false
                    )
                  }}
                >
                  Clear
                </FbButton>
              </div>
            )
          },
        },
        {
          id: 'matched_door',
          accessorKey: 'matched_door',
          header: 'Door Match',
          size: 300,
          cell: (info) => {
            const row = info.row.original
            const isImported = row[InternalColumns.SET_CHAIN_PROXY_ID] !== -1
            const disabled =
              isImported || row[InternalColumns.IS_CUSTOM_COMPANY]
            const doors = row[InternalColumns.SUGGESTED_DOORS]?.filter((c) => {
              if (row[InternalColumns.MATCHED_CHAIN]) {
                return c.chain_id === row[InternalColumns.MATCHED_CHAIN].item_id
              }
              return true
            })

            return (
              <div
                key={info.cell.id}
                className={cn(
                  'flex-1 flex gap-2 items-center justify-start h-full w-full p-2',
                  {
                    'opacity-20': disabled,
                  }
                )}
              >
                {row[InternalColumns.MATCHED_DOOR] && (
                  <HelpTooltip
                    content={
                      <DoorInfo door={row[InternalColumns.MATCHED_DOOR]} />
                    }
                    delay={[0, 100]}
                  />
                )}
                {row[InternalColumns.IS_DOOR_MANUAL_MATCH] ||
                row[InternalColumns.IS_CHAIN_MANUAL_MATCH] ? (
                  <div className="text-sm truncate">
                    {row[InternalColumns.MATCHED_DOOR]?.name ??
                      'No Door Selected'}
                  </div>
                ) : (
                  <Select
                    buttonSize="xs"
                    buttonVariant={
                      row[InternalColumns.MATCHED_DOOR]?.item_id
                        ? 'primary'
                        : 'outline'
                    }
                    className="w-[400px] max-h-[500px]"
                    disabled={
                      !row[InternalColumns.SUGGESTED_DOORS]?.length || disabled
                    }
                    nullSelectionLabel={
                      row[InternalColumns.MATCHED_CHAIN]?.item_id
                        ? `${row[InternalColumns.SUGGESTED_DOORS]?.length ?? 0} doors for selected chain`
                        : row[InternalColumns.SUGGESTED_DOORS]?.length
                          ? `Found ${row[InternalColumns.SUGGESTED_DOORS]?.length ?? 0} door suggestions`
                          : 'No Suggestions'
                    }
                    items={doors?.map((door) => ({
                      label: <DoorInfo door={door} />,
                      labelWhenSelected: (
                        <div className="flex flex-row gap-2">
                          <div className="text-sm truncate">{door.name}</div>
                        </div>
                      ),
                      value: door.item_id,
                    }))}
                    onSelect={(value) => {
                      const door = row[InternalColumns.SUGGESTED_DOORS]?.find(
                        (d) => d.item_id === value
                      )
                      if (door) {
                        const chain =
                          row[InternalColumns.SUGGESTED_CHAINS]?.find(
                            (c) => c.item_id === door?.chain_id
                          ) ?? null
                        viewModel.updateMatchedDoor(
                          row[InternalColumns.ROW_ID],
                          door,
                          false
                        )
                        viewModel.updateMatchedChain(
                          row[InternalColumns.ROW_ID],
                          chain,
                          false
                        )
                      }
                    }}
                    selected={row[InternalColumns.MATCHED_DOOR]?.item_id}
                  />
                )}
                <FbButton
                  variant="outline"
                  disabled={disabled}
                  size="xs"
                  onClick={() =>
                    viewModel.updateMatchedDoor(
                      row[InternalColumns.ROW_ID],
                      null,
                      false
                    )
                  }
                >
                  Clear
                </FbButton>
                <FbButton
                  variant="outline"
                  size="xs"
                  onClick={() =>
                    viewModel.setSelectedRowId(row[InternalColumns.ROW_ID])
                  }
                >
                  Manual Search
                </FbButton>
              </div>
            )
          },
        }
      )
    }

    if (viewModel.selectedTab === 'reviewed') {
      const crmDataIndicatorColumnDefs: ColumnDef<CSVRow>[] = headers
        .filter((h) => crmDataIndicatorColumnKeys.includes(h.original as any))
        .map((header) => ({
          id: header.original,
          accessorKey: header.original,
          size: 130,
          header: () => {
            return (
              <div className="text-xs text-gray-500">
                {header.label ?? header.original}
              </div>
            )
          },
          cell: (info) => {
            return (
              <div className="text-xs text-gray-500 flex-1 flex gap-2 items-center justify-start h-full w-full p-2">
                {info.getValue() !== -1 ? <CheckCircle size={16} /> : '-'}
              </div>
            )
          },
        }))

      columnDefs.unshift(...crmDataIndicatorColumnDefs)
    }

    return columnDefs ?? []
  }, [viewModel.importData, viewModel.importPreview, viewModel.selectedTab])

  return (
    <Fragment>
      <div className="flex flex-row gap-2 items-start justify-end">
        <Tabs
          isSmall
          tabs={[
            {
              key: 'pending_review',
              title: `Pending (${viewModel.tableData.pending_review.length})`,
            },
            {
              key: 'reviewed',
              title: `Reviewed (${viewModel.tableData.reviewed.length})`,
            },
          ]}
          selectedTab={viewModel.selectedTab}
          setSelectedTab={(tab) => {
            viewModel.setSelectedTab(tab as CSVRowState)
          }}
        />

        <BackButton hasPendingChanges={viewModel.hasPendingChanges} />

        <FbButton
          onClick={() => viewModel.handleSave()}
          variant="primary"
          size="sm"
          className="flex items-center gap-2 flex-shrink-0"
          disabled={!viewModel.hasPendingChanges}
          loading={viewModel.isSaving}
        >
          <Save size={16} />
          Save
        </FbButton>

        <FbButton
          onClick={() => viewModel.setShowConfigureExportModal(true)}
          variant="secondary"
          size="sm"
          className="flex items-center gap-2 flex-shrink-0"
        >
          <Download size={16} />
          Export
        </FbButton>
      </div>

      <div
        style={{
          height: 'calc(var(--base-page-table-height) - 30px)',
        }}
      >
        <ImporterDataTable
          tableHeader={
            <div className="flex flex-row gap-2 justify-between py-2 w-full">
              <div className="flex flex-row gap-2">
                {viewModel.importPreview?.from_unmatched_chains && (
                  <div className="flex flex-row gap-2">
                    <Select
                      buttonSize="sm"
                      className="w-[400px] max-h-[500px]"
                      nullSelectionLabel="Filter by Opp Type"
                      items={Object.entries(
                        viewModel.chainMergerAvailableSubcategories
                      ).map(([value, label]) => ({
                        label,
                        value,
                      }))}
                      onSelect={(value) =>
                        viewModel.setSubcategoryFilter(Number(value))
                      }
                      selected={viewModel.selectedSubcategoryFilter?.toString()}
                    />
                  </div>
                )}

                {viewModel.selectedTab !== 'reviewed' ? (
                  <>
                    {!viewModel.importPreview?.from_unmatched_chains && (
                      <FbButton
                        disabled={!viewModel.shouldShowBulkActions}
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          viewModel.updateIsCustomCompany(
                            viewModel.selectedRowIds,
                            false
                          )
                        }
                      >
                        Unset Custom Company
                      </FbButton>
                    )}

                    {!viewModel.importPreview?.from_unmatched_chains && (
                      <Select
                        buttonSize="sm"
                        className="w-[400px] max-h-[500px]"
                        disabled={!viewModel.shouldShowBulkActions}
                        items={viewModel.companySubcategories}
                        nullSelectionLabel="Set as Custom Company"
                        onSelect={(value, originalItem) =>
                          viewModel.updateCustomCompanyCategory(
                            viewModel.selectedRowIds,
                            originalItem?.category_id,
                            value
                          )
                        }
                      />
                    )}
                  </>
                ) : (
                  <div></div>
                )}
              </div>

              <div className="flex flex-row gap-2">
                {viewModel.selectedTab === 'pending_review' && (
                  <FbButton
                    onClick={viewModel.handleReview}
                    variant="warning"
                    size="sm"
                    className="flex items-center gap-2 flex-shrink-0"
                    disabled={
                      !importNumber ||
                      viewModel.tableData['reviewed'].length ===
                        viewModel.importData?.rows.length
                    }
                  >
                    <CheckCircle size={16} />
                    Open Review
                  </FbButton>
                )}

                {viewModel.selectedTab === 'reviewed' && (
                  <>
                    {viewModel.importPreview?.from_unmatched_chains ? (
                      <FbButton
                        onClick={viewModel.handleSetUnmatchedChains}
                        variant="secondary"
                        size="sm"
                        className="flex items-center gap-2 flex-shrink-0"
                        loading={viewModel.isSettingUnmatchedChains}
                        disabled={
                          !importNumber ||
                          viewModel.tableData['reviewed'].length === 0
                        }
                      >
                        <Users2 size={16} />
                        Merge Unmatched Chains
                      </FbButton>
                    ) : (
                      <FbButton
                        onClick={viewModel.handleSetCompanies}
                        variant="secondary"
                        size="sm"
                        className="flex items-center gap-2"
                        loading={viewModel.isSettingCompanies}
                        disabled={
                          !importNumber ||
                          viewModel.tableData['pending_review'].length === 0
                        }
                      >
                        <Upload size={16} />
                        Set Companies
                      </FbButton>
                    )}
                  </>
                )}

                {viewModel.selectedTab === 'reviewed' && (
                  <div className="flex flex-row items-center gap-2 ml-2">
                    <FbButton
                      onClick={viewModel.handleSetTags}
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-2 flex-shrink-0"
                      loading={viewModel.isSettingTags}
                      disabled={
                        !importNumber ||
                        viewModel.tableData['reviewed'].length === 0 ||
                        !viewModel.canAddTags[0]
                      }
                      tooltip={
                        viewModel.canAddTags[1].length > 0
                          ? viewModel.canAddTags[1].join('\n')
                          : undefined
                      }
                    >
                      <Tag size={16} />
                      Import Tags
                    </FbButton>

                    <FbButton
                      onClick={viewModel.handleSetDeals}
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-2 flex-shrink-0"
                      loading={viewModel.isSettingDeals}
                      disabled={
                        !importNumber ||
                        viewModel.tableData['reviewed'].length === 0 ||
                        !viewModel.canAddDeals[0]
                      }
                      tooltip={
                        viewModel.canAddDeals[1].length > 0
                          ? viewModel.canAddDeals[1].join('\n')
                          : undefined
                      }
                    >
                      <Briefcase size={16} />
                      Import Deals
                    </FbButton>

                    <FbButton
                      onClick={viewModel.handleSetContacts}
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-2 flex-shrink-0"
                      loading={viewModel.isSettingContacts}
                      disabled={
                        !importNumber ||
                        viewModel.tableData['reviewed'].length === 0 ||
                        !viewModel.canAddContacts[0]
                      }
                      tooltip={
                        viewModel.canAddContacts[1].length > 0
                          ? viewModel.canAddContacts[1].join('\n')
                          : undefined
                      }
                    >
                      <User size={16} />
                      Import Contacts
                    </FbButton>

                    <FbButton
                      onClick={viewModel.handleSetCompanyNotes}
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-2 flex-shrink-0"
                      loading={viewModel.isSettingCompanyNotes}
                      disabled={
                        !importNumber ||
                        viewModel.tableData['reviewed'].length === 0 ||
                        !viewModel.canAddCompanyNotes[0]
                      }
                      tooltip={
                        viewModel.canAddCompanyNotes[1].length > 0
                          ? viewModel.canAddCompanyNotes[1].join('\n')
                          : undefined
                      }
                    >
                      <StickyNote size={16} />
                      Import Company Notes
                    </FbButton>
                  </div>
                )}
              </div>
            </div>
          }
          data={viewModel.tableData[viewModel.selectedTab]}
          columns={columns}
          setRowSelection={viewModel.setRowSelection}
          columnFiltering={viewModel.columnFiltering}
        />
      </div>
    </Fragment>
  )
}

function DoorInfo(props: { door: DoorSearchResult }) {
  const { door } = props
  return (
    <div className="flex flex-col gap-1">
      <div className="font-bold">{door.name}</div>
      <div className="text-xs flex flex-col gap-1">
        <div>
          {door.score} - {door.address}
        </div>
        <div>
          {door.city}, {door.state} {door.zipcode} {door.country}
        </div>
        {door.domain && <div>Domain: {door.domain}</div>}
        <div>
          Chain: {door.chain_name} ({door.chain_size} locations)
        </div>
        {door.type && <div>Type: {door.type}</div>}
      </div>
    </div>
  )
}

function BackButton({ hasPendingChanges }: { hasPendingChanges: boolean }) {
  return (
    <Link to="/importer">
      <FbButton
        onClick={(e) => {
          if (hasPendingChanges) {
            if (
              !confirm(
                'You have unsaved changes. Are you sure you want to go back?'
              )
            ) {
              e.preventDefault()
              e.stopPropagation()
            }
          }
        }}
        variant="secondary"
        size="sm"
        className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
      >
        <ArrowLeft size={16} />
        Go Back
      </FbButton>
    </Link>
  )
}

function ChainInfo(props: { chain: ChainSearchResult }) {
  const { chain } = props
  return (
    <div className="flex flex-col gap-1">
      <div className="font-bold">{chain.name}</div>
      <div className="text-xs flex flex-col gap-1">
        <div>Score: {chain.score}</div>
        <div>
          {chain.domain} - {chain.type}
        </div>
      </div>
    </div>
  )
}
